import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, FormLabel, Stack } from '@mui/material';
import { Popup } from 'components/common/popup/Popup';
import MITable from 'components/common/grid/MITable';
import { GridData } from 'components/common/grid';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import IconMi from 'components/common/icon/IconMi';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import useBundleTranslation from 'i18n';
import { reportAPI } from 'api/report';
import {
    ComponentSettingsKpiChartMeasureField,
    DisplayMask,
    IKpiChartOverlay,
    IKpiChartOverlaysSettings,
} from '@report-content';
import useChartColorPalette from 'components/report-content/hooks/useChartColorPalette';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { KpiChartOverlaysContext, KpiChartOverlaysContextType } from './KpiChartOverlaysContext';

function getDefaultOverlayStyle(
    usedColors: Array<string>,
    colorPalette: Array<string>,
): Pick<IKpiChartOverlay, 'chart_type' | 'dash_style' | 'axis' | 'color'> {
    // Picks the first palette color not already in use; once every color is taken, it's fine to repeat.
    const pickOverlayColor = (usedColors: Array<string>, colorPalette: Array<string>): string => {
        const unusedColor = colorPalette.find((color) => !usedColors.includes(color));
        return unusedColor ?? colorPalette[usedColors.length % colorPalette.length];
    };

    return {
        chart_type: 'Line',
        dash_style: 'line',
        axis: 'Left',
        color: pickOverlayColor(usedColors, colorPalette),
    };
}

function buildGridData(overlays: Array<IKpiChartOverlay>): GridData {
    return overlays.map((overlay) => ({
        id: overlay.id,
        title: overlay.title,
        // Raw values, read by KpiChartOverlayGridDataCell's edit form to prefill its fields.
        chart_type: overlay.chart_type,
        dash_style: overlay.dash_style,
        color: overlay.color,
        // Display-only versions of the same values, rendered by the grid's own columns below.
        type_display: overlay.chart_type === 'Bar' ? 'Bar' : overlay.dash_style,
        axis: overlay.axis,
        color_display: {
            component: 'text',
            config: { text: overlay.color, color: overlay.color },
        },
        data: { component: 'kpiChartOverlayGridData', config: {} },
    }));
}

export default function OverlayMetricsPopup({
    isOpen,
    setIsOpen,
    elementId,
    overlays,
    measureFields,
    displayMasks,
    leftAxisDisplayMaskId,
    rightAxisDisplayMaskId,
    onSave,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    elementId: number;
    overlays: Array<IKpiChartOverlay>;
    measureFields: Array<ComponentSettingsKpiChartMeasureField>;
    displayMasks: Array<DisplayMask>;
    leftAxisDisplayMaskId?: string;
    rightAxisDisplayMaskId?: string;
    onSave?: (newSettings: IKpiChartOverlaysSettings) => void;
}) {
    const { t } = useBundleTranslation(['components/report-content']);
    const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
    const addElementProps = useCustomSimplifiedForm();

    const overlayColorPalette = useChartColorPalette();

    // Draft copy, edited locally while the popup is open; only propagated to the caller (and
    // persisted) on close, if it actually changed. This component only mounts while the popup is
    // open (see isOverlayPopupOpen && <OverlayMetricsPopup .../> in KpiChart.tsx), so this always
    // starts fresh from the last-committed settings.
    const [draftSettings, setDraftSettings] = useState<IKpiChartOverlaysSettings>({
        overlays,
        left_axis_display_mask_id: leftAxisDisplayMaskId,
        right_axis_display_mask_id: rightAxisDisplayMaskId,
    });
    const draftOverlays = draftSettings.overlays;

    const displayMaskOptions = useMemo(
        () => [
            { label: t('kpi_chart.overlay_display_mask_default'), value: '' },
            ...displayMasks
                .filter((mask) => mask.mask_type === 'numeric')
                .map((mask) => ({ label: mask.display_example, value: mask.display_mask_id })),
        ],
        [displayMasks],
    );

    const { data: availableOverlays, isLoading } = useQuery({
        queryKey: ['kpi-chart-available-overlays', elementId],
        queryFn: () => reportAPI.getAvailableKpiChartOverlays(elementId),
        enabled: isAddOpen,
    });

    const availableValues = useMemo(() => {
        const addedIds = new Set(draftOverlays.map((overlay) => overlay.id));
        return (availableOverlays ?? []).filter((value) => !addedIds.has(value.value));
    }, [availableOverlays, draftOverlays]);

    const gridData = useMemo(() => buildGridData(draftOverlays), [draftOverlays]);

    // Chart series colors count as "used" too, so new overlays don't default to a color
    // already shown on the chart itself, not just one used by another overlay.
    const seriesColors = useMemo(
        () =>
            (measureFields ?? [])
                .map((field) => field.color)
                .filter((color): color is string => !!color)
                .map((color) => color.toUpperCase()),
        [measureFields],
    );

    const handleClose = () => {
        const initialSettings: IKpiChartOverlaysSettings = {
            overlays,
            left_axis_display_mask_id: leftAxisDisplayMaskId,
            right_axis_display_mask_id: rightAxisDisplayMaskId,
        };
        if (JSON.stringify(draftSettings) !== JSON.stringify(initialSettings)) {
            onSave?.(draftSettings);
        }
        setIsOpen(false);
    };

    const overlaysContext: KpiChartOverlaysContextType = {
        onUpdate: (id, changes) => {
            setDraftSettings((prev) => ({
                ...prev,
                overlays: prev.overlays.map((overlay) => (overlay.id === id ? { ...overlay, ...changes } : overlay)),
            }));
        },
        onDelete: (id) => {
            setDraftSettings((prev) => ({
                ...prev,
                overlays: prev.overlays.filter((overlay) => overlay.id !== id),
            }));
        },
    };

    const handleAddSelected = () => {
        let selectedIds: Array<string> = [];
        try {
            selectedIds = JSON.parse(addElementProps.form.hookFormGetValues('kpiChartOverlayCheckBox') ?? '[]');
        } catch (e) {}

        setDraftSettings((prev) => {
            const newOverlays: Array<IKpiChartOverlay> = selectedIds
                .map((id) => availableValues.find((value) => value.value === id))
                .filter((value): value is NonNullable<typeof value> => !!value)
                .reduce<Array<IKpiChartOverlay>>((acc, value) => {
                    const usedColors = [
                        ...seriesColors,
                        ...prev.overlays.map((overlay) => overlay.color),
                        ...acc.map((overlay) => overlay.color),
                    ];
                    acc.push({
                        id: value.value,
                        title: value.label,
                        ...getDefaultOverlayStyle(usedColors, overlayColorPalette),
                    });
                    return acc;
                }, []);
            return newOverlays.length ? { ...prev, overlays: [...prev.overlays, ...newOverlays] } : prev;
        });
        setIsAddOpen(false);
    };

    return (
        <Popup
            onHide={handleClose}
            open={isOpen}
            settings={{
                title: t('kpi_chart.overlays'),
                noCancel: true,
                textOK: t('done'),
            }}
        >
            <KpiChartOverlaysContext.Provider value={overlaysContext}>
                <MITable
                    gridName={'kpi_chart_overlay_metrics'}
                    autoHeight
                    height={gridData.length ? undefined : 60}
                    showHeader={gridData.length > 0}
                    data={gridData}
                    isLoading={false}
                    t={t}
                    emptyGridMessage={t('kpi_chart.no_overlays')}
                    columns={[
                        { name: 'title', title: 'kpi_chart.overlay_report_metric' },
                        { name: 'type_display', title: 'type' },
                        { name: 'axis', title: 'kpi_chart.overlay_axis' },
                        { name: 'color_display', title: 'kpi_chart.overlay_color' },
                        { name: 'data', title: '', width: 60, hidable: false },
                    ]}
                    massActionsConfig={{ enabled: false }}
                />
                <Button
                    variant="light"
                    startIcon={<IconMi icon="new" />}
                    onClick={() => setIsAddOpen(true)}
                    sx={{ mb: 1 }}
                >
                    {t('kpi_chart.add_overlay')}
                </Button>
            </KpiChartOverlaysContext.Provider>
            <Stack direction={'row'} spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <FormLabel>{t('kpi_chart.overlay_left_axis_display_mask')}</FormLabel>
                    <ReactSelect
                        data={displayMaskOptions}
                        value={draftSettings.left_axis_display_mask_id ?? ''}
                        update={(value) =>
                            setDraftSettings((prev) => ({
                                ...prev,
                                left_axis_display_mask_id: value == '' ? undefined : value,
                            }))
                        }
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <FormLabel>{t('kpi_chart.overlay_right_axis_display_mask')}</FormLabel>
                    <ReactSelect
                        data={displayMaskOptions}
                        value={draftSettings.right_axis_display_mask_id ?? ''}
                        update={(value) =>
                            setDraftSettings((prev) => ({
                                ...prev,
                                right_axis_display_mask_id: value == '' ? undefined : value,
                            }))
                        }
                    />
                </Box>
            </Stack>
            {isAddOpen && isLoading && <LoadingPlaceholder />}
            {isAddOpen && !isLoading && (
                <Popup
                    settings={{
                        title: t('kpi_chart.add_overlay'),
                        textOK: t('add_selected'),
                    }}
                    open={isAddOpen}
                    onHide={() => setIsAddOpen(false)}
                    onConfirm={handleAddSelected}
                >
                    <TemplateFormComponent
                        component={'CheckBoxList'}
                        elementProps={addElementProps}
                        name={'kpiChartOverlayCheckBox'}
                        props={{
                            useFuzzySearch: true,
                            emptyHeight: 120,
                            showCounter: false,
                            showSearch: true,
                            selectAllLabel: t('kpi_chart.overlay_report_metrics'),
                            async: false,
                            data: { values: availableValues, filters: [], columns: [] },
                        }}
                    />
                </Popup>
            )}
        </Popup>
    );
}
